import { forwardRef, useCallback, useEffect, useImperativeHandle, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { isBlankCanvas } from '../../utils/documentReviewModel';
import { defaultBrand } from '../../config/brand.config';

/**
 * FIX-D337 (plan §4.1) - draw a signature with a finger / mouse (pointer events, no library).
 * `toPng()` returns a PNG data URL, or null when nothing was drawn.
 */
export interface SignatureCanvasHandle {
  clear: () => void;
  toPng: () => string | null;
  isEmpty: () => boolean;
}

interface Props {
  width?: number;
  height?: number;
  onChange?: (empty: boolean) => void;
  label?: string;
}

const SignatureCanvas = forwardRef<SignatureCanvasHandle, Props>(function SignatureCanvas({ width = 480, height = 170, onChange, label }, ref) {
  const { t } = useTranslation();
  const canvas = useRef<HTMLCanvasElement | null>(null);
  const drawing = useRef(false);
  const last = useRef<{ x: number; y: number } | null>(null);

  const ctx = () => canvas.current?.getContext('2d') ?? null;

  const empty = useCallback((): boolean => {
    const c = canvas.current;
    const g = ctx();
    if (!c || !g) return true;
    return isBlankCanvas(g.getImageData(0, 0, c.width, c.height).data);
  }, []);

  useEffect(() => {
    const g = ctx();
    if (!g) return;
    g.lineWidth = 2.6;
    g.lineCap = 'round';
    g.lineJoin = 'round';
    // a dark ink in every theme: the signature goes on a white PDF page
    g.strokeStyle = defaultBrand.primaryScale[950];
  }, []);

  useImperativeHandle(ref, () => ({
    clear: () => {
      const c = canvas.current;
      ctx()?.clearRect(0, 0, c?.width ?? 0, c?.height ?? 0);
      onChange?.(true);
    },
    toPng: () => (empty() ? null : canvas.current?.toDataURL('image/png') ?? null),
    isEmpty: empty,
  }), [empty, onChange]);

  const point = (e: React.PointerEvent<HTMLCanvasElement>) => {
    const c = canvas.current!;
    const r = c.getBoundingClientRect();
    return { x: ((e.clientX - r.left) * c.width) / r.width, y: ((e.clientY - r.top) * c.height) / r.height };
  };

  return (
    <canvas
      ref={canvas}
      width={width}
      height={height}
      role="img"
      aria-label={label ?? t('docReview.signature.canvasLabel')}
      className="w-full max-w-full touch-none rounded-lg border border-dashed border-surface-300 bg-surface-0"
      style={{ aspectRatio: `${width} / ${height}` }}
      onPointerDown={(e) => {
        e.currentTarget.setPointerCapture(e.pointerId);
        drawing.current = true;
        last.current = point(e);
      }}
      onPointerMove={(e) => {
        if (!drawing.current) return;
        const g = ctx();
        const p = point(e);
        if (g && last.current) {
          g.beginPath();
          g.moveTo(last.current.x, last.current.y);
          g.lineTo(p.x, p.y);
          g.stroke();
        }
        last.current = p;
      }}
      onPointerUp={() => {
        drawing.current = false;
        last.current = null;
        onChange?.(empty());
      }}
      onPointerCancel={() => { drawing.current = false; last.current = null; }}
    />
  );
});

export default SignatureCanvas;
